import React from 'react';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import type { StagedStatus } from '../hooks/useArtisanStaging';

export interface FileTab {
    id: string;
    fileName: string;
    status: StagedStatus;
}

interface Props {
    tabs: FileTab[];
    activeId: string | null;
    onSelect: (id: string) => void;
    onFilesPicked: (files: File[]) => void;
    /** Locked while the batch is being read or saved — the set of tabs is in flux. */
    disabled: boolean;
}

/**
 * A dot appears only when a tab needs the roaster's attention or has reached the database.
 * A file that is simply waiting to be reviewed carries no mark, so the marks that do
 * appear stay meaningful.
 */
const STATUS_DOT: Record<StagedStatus, string | null> = {
    ready: null,
    invalid: 'bg-amber-500',
    saving: 'bg-primary-500 animate-pulse',
    saved: 'bg-green-500',
    duplicate: 'bg-blue-500',
    failed: 'bg-red-500',
};

const TAB_BASE =
    'relative flex-shrink-0 inline-flex items-center gap-1.5 rounded-t-lg border border-b-0 border-gray-200 ' +
    'px-3.5 py-2 text-sm tabular-nums transition-colors focus:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-primary-500 focus-visible:ring-offset-1';

/**
 * Folder tabs along the top edge of the review area: one per staged roast, numbered in the
 * order the files were added. Numbers rather than file names because a batch can run to
 * twenty exports whose names differ by four characters — the active file is named in full
 * on the sheet below, and each tab carries its name as a tooltip.
 */
export const ArtisanFileTabs: React.FC<Props> = ({ tabs, activeId, onSelect, onFilesPicked, disabled }) => {
    const { t } = useLanguage();

    return (
        <div className="flex items-end gap-2 border-b border-gray-200">
            <div className="flex-1 min-w-0 flex items-end gap-1 overflow-x-auto pt-1">
                {tabs.map((tab, index) => {
                    const isActive = tab.id === activeId;
                    const dot = STATUS_DOT[tab.status];
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => onSelect(tab.id)}
                            title={tab.fileName}
                            aria-current={isActive ? 'true' : undefined}
                            className={`${TAB_BASE} ${isActive
                                ? 'bg-white text-gray-900 font-semibold border-t-2 border-t-primary-600'
                                : 'bg-gray-50 text-gray-500 border-t-2 border-t-transparent hover:bg-gray-100 hover:text-gray-700'
                                }`}
                            data-test-id="artisan-file-tab"
                        >
                            {index + 1}
                            {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />}
                            <span className="sr-only">{tab.fileName}</span>
                        </button>
                    );
                })}
            </div>

            {/* Pinned outside the scroll area so it stays reachable however long the batch gets. */}
            <label
                className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-t-lg border border-b-0 border-dashed
                    border-gray-300 px-3.5 py-2 text-sm font-medium transition-colors ${disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-primary-600 hover:bg-primary-50 hover:border-primary-300 cursor-pointer'
                    }`}
                data-test-id="artisan-add-files-tab"
            >
                <Plus className="h-4 w-4" />
                {t('artisanSync.stage.addFiles')}
                <input
                    type="file"
                    accept=".json,.alog"
                    multiple
                    disabled={disabled}
                    className="hidden"
                    onChange={(e) => {
                        onFilesPicked(Array.from(e.target.files ?? []));
                        e.target.value = '';
                    }}
                />
            </label>
        </div>
    );
};
